import { useState, useCallback } from "react";
import { convertDollarToCents, formatDecimalInput } from "src/helpers";
import { UseMenuItemOptionModalHandlerParams } from "src/interfaces/menu-items-props.interface";

export const useMenuItemFormHandlers = ({
  formik,
  setMenuItemImage,
  onOpenMenuItemOptionModal,
}: UseMenuItemOptionModalHandlerParams) => {

  const [isOtherLanguageEnabled, setIsOtherLanguageEnabled] = useState(false);
  const [displayPriceCents, setDisplayPriceCents] = useState("");
  /** Tax rate displayed as percentage (e.g. 8.25); formik stores decimal (0.0825) */
  const [displayTaxRatePercent, setDisplayTaxRatePercent] = useState("");

  const { setFieldValue,setFieldTouched, values } = formik;

  const handleOtherLanguageToggle = useCallback(() => {
    setIsOtherLanguageEnabled((prev) => !prev);
  }, []);

  const handleMenuItemImageChange = useCallback(
    (imageFile: File) => {
      const imageUrl = URL.createObjectURL(imageFile);
      setMenuItemImage({
        url: imageUrl,
        file: imageFile,
      });
      requestAnimationFrame(() => URL.revokeObjectURL(imageUrl));
    },
    [setMenuItemImage]
  );

  const removeMenuItemOption = useCallback(
    (menuItemOptionIndex: number) => {
      const menuItemOptions = [...values.itemOptionInputList];
      menuItemOptions.splice(menuItemOptionIndex, 1);
      setFieldValue("itemOptionInputList", menuItemOptions);
    },
    [values.itemOptionInputList, setFieldValue]
  );

  const addMenuItemOptionForm = useCallback(() => {
    setFieldValue("itemOptionInputList", [
      ...values.itemOptionInputList,
      {
        name: "",
        description: "",
        renderSequence: 1,
        isRequired: false,
        multipleSelectionsAllowed: false,
        addItemOptionElementInputList: [],
      },
    ]);
    onOpenMenuItemOptionModal(-1);
  }, [setFieldValue, values.itemOptionInputList, onOpenMenuItemOptionModal]);

  const handlePriceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const formattedValue = formatDecimalInput(e.target.value, 2);
      setDisplayPriceCents(formattedValue);
      const centsValue = convertDollarToCents(formattedValue);
      setFieldValue("priceCents", centsValue, true);
      setFieldTouched("priceCents", true, false);
    },
    [setFieldValue, setFieldTouched]
  );

  const handlePriceBlur = useCallback(() => {
    if (!displayPriceCents) return;
    let formattedValue = displayPriceCents;
    const parts = displayPriceCents.split(".");
    if (!displayPriceCents.includes(".")) {
      formattedValue = `${displayPriceCents}.00`;
    } else if (parts[1].length === 1) {
      formattedValue = `${displayPriceCents}0`;
    } else if (parts[1].length > 2) {
      formattedValue = `${parts[0]}.${parts[1].slice(0, 2)}`;
    }
    setDisplayPriceCents(formattedValue);
    setFieldValue("priceCents", convertDollarToCents(formattedValue), true);
  }, [displayPriceCents, setFieldValue]);

  const handleTagsChange = useCallback((selectedTags: string[]) => {
    setFieldValue("itemTagCorrelationIdList", selectedTags);
  }, [setFieldValue]);

  const handleEditMenuItemOptionModal = useCallback((menuItemOptionIndex: number) => {
    onOpenMenuItemOptionModal(menuItemOptionIndex);
  }, [onOpenMenuItemOptionModal]);

  const handleTaxRateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      let value = e.target.value;
      if (value.includes(".")) {
        const [intPart, decimalPart] = value.split(".");
        if (decimalPart.length > 4) {
          value = `${intPart}.${decimalPart.slice(0, 4)}`;
        }
      }
      setFieldValue("taxRate", Number(value), true);
      setFieldTouched("taxRate", true, false);
    },
    [setFieldValue, setFieldTouched]
  );

  /** Tax rate as percentage (8.25). Converts to decimal for formik (0.0825). */
  const handleTaxRatePercentChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const raw = e.target.value;
      setDisplayTaxRatePercent(raw);
      const num = parseFloat(raw);
      if (!Number.isNaN(num)) {
        setFieldValue("taxRate", num / 100, true);
        setFieldTouched("taxRate", true, false);
      }
    },
    [setFieldValue, setFieldTouched]
  );

  const handleTaxRatePercentBlur = useCallback(() => {
    const num = parseFloat(displayTaxRatePercent);
    if (!Number.isNaN(num) && num >= 0 && num <= 100) {
      setDisplayTaxRatePercent(num.toFixed(2));
    }
  }, [displayTaxRatePercent]);

  return {
    isOtherLanguageEnabled,
    setIsOtherLanguageEnabled,
    displayPriceCents,
    setDisplayPriceCents,
    displayTaxRatePercent,
    setDisplayTaxRatePercent,
    handleOtherLanguageToggle,
    handleMenuItemImageChange,
    removeMenuItemOption,
    addMenuItemOptionForm,
    handlePriceChange,
    handlePriceBlur,
    handleTagsChange,
    handleEditMenuItemOptionModal,
    handleTaxRateChange,
    handleTaxRatePercentChange,
    handleTaxRatePercentBlur,
  };
};

export type UseMenuItemFormHandlersReturn = ReturnType<typeof useMenuItemFormHandlers>;
